// MasterScreen - §18 도출. §5·§7·§10·§16 이 참조하는 마스터 데이터와
// 판정 임계값을 관리한다.
//
// 8개 섹션을 탭으로 나눈다. 탭마다 별도 컴포넌트라서 **활성 탭의 데이터만
// 조회한다** - 8개를 한꺼번에 읽으면 마스터 화면 진입이 8회 호출이 된다.
//
// `member` 는 전 섹션 읽기 전용이다 (§04 §7). 서버도 `require_admin` 으로
// 막지만, 눌러 보고 403 을 받는 것보다 애초에 비활성인 편이 낫다.
import { THead, Table, TableScroll, Td, Th, Tr } from "@/components/Table";
import {
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  DataBoundary,
  DateField,
  Dialog,
  EmptyState,
  Field,
  Icon,
  Num,
  Select,
  TableSkeleton,
  Tabs,
  TextInput,
} from "@/components/ui";
import {
  type CompanySettings,
  type Customer,
  type Me,
  type MilestoneLabel,
  type ProcessMaster,
  type Staff,
  putMilestoneLabels,
  updateSettings,
  updateUser,
} from "@/lib/api";
import { EMPTY } from "@/lib/format";
import type { CrudResource } from "@/lib/hooks";
import {
  useHolidays,
  useLeadTimeTemplates,
  useMilestoneLabels,
  useSettings,
  useUsers,
} from "@/lib/hooks";
import { DEFAULT_WEIGHTS, MILESTONE_DEFS, type MilestoneKey } from "@/lib/status";
import type { ScreenNav } from "@/screens/nav";
import { useState } from "react";

type SectionKey =
  | "staff"
  | "customers"
  | "processes"
  | "leadtime"
  | "labels"
  | "holidays"
  | "judge"
  | "users";

const SECTIONS: readonly { key: SectionKey; label: string }[] = [
  { key: "staff", label: "담당자" },
  { key: "customers", label: "고객사" },
  { key: "processes", label: "공정" },
  { key: "leadtime", label: "리드타임" },
  { key: "labels", label: "마일스톤 라벨" },
  { key: "holidays", label: "공휴일" },
  { key: "judge", label: "판정 설정" },
  { key: "users", label: "사용자" },
];

export const MasterScreen = ({
  nav,
  me,
  staffRes,
  customersRes,
  processesRes,
}: {
  nav: ScreenNav;
  me: Me | null;
  staffRes: CrudResource<Staff>;
  customersRes: CrudResource<Customer>;
  processesRes: CrudResource<ProcessMaster>;
}) => {
  const [tab, setTab] = useState<SectionKey>("staff");
  const canEdit = me?.role === "admin";

  return (
    <div className="flex flex-col gap-s4">
      <Tabs
        label="마스터 섹션"
        items={SECTIONS.map((s) => ({ key: s.key, label: s.label }))}
        active={tab}
        onChange={(k) => setTab(k as SectionKey)}
      />

      {!canEdit && (
        <p
          className="rounded-control px-s4 py-s3 text-label"
          style={{ background: "var(--surface-inset)", color: "var(--text-secondary)" }}
        >
          조회 전용입니다. 편집은 관리자 권한이 필요해요.
        </p>
      )}

      {tab === "staff" && (
        <SimpleSection
          title="담당자"
          resource={staffRes}
          canEdit={canEdit}
          nav={nav}
          columns={[
            { key: "name", label: "성명", required: true, width: 110 },
            { key: "dept", label: "부서", width: 120 },
            { key: "position", label: "직급", width: 80 },
            { key: "email", label: "이메일", kind: "email", width: 200 },
            { key: "phone", label: "연락처", kind: "tel", width: 140 },
          ]}
        />
      )}

      {tab === "customers" && (
        <SimpleSection
          title="고객사"
          resource={customersRes}
          canEdit={canEdit}
          nav={nav}
          columns={[
            { key: "name", label: "고객사명", required: true, width: 130 },
            { key: "code", label: "코드", width: 80 },
            { key: "contact_name", label: "담당자명", width: 100 },
            { key: "contact_phone", label: "연락처", kind: "tel", width: 140 },
            { key: "contact_email", label: "이메일", kind: "email", width: 190 },
            { key: "note", label: "비고" },
          ]}
        />
      )}

      {tab === "processes" && (
        <SimpleSection
          title="공정"
          resource={processesRes}
          canEdit={canEdit}
          nav={nav}
          columns={[
            { key: "name", label: "공정명", required: true, width: 150 },
            { key: "seq", label: "순서", kind: "number", width: 80 },
          ]}
        />
      )}

      {tab === "leadtime" && <LeadTimeSection canEdit={canEdit} nav={nav} />}
      {tab === "labels" && <LabelSection canEdit={canEdit} nav={nav} />}
      {tab === "holidays" && <HolidaySection canEdit={canEdit} nav={nav} />}
      {tab === "judge" && <JudgeSection canEdit={canEdit} nav={nav} />}
      {tab === "users" && <UserSection canEdit={canEdit} nav={nav} staff={staffRes.data} />}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────
   담당자 / 고객사 / 공정 - 같은 CRUD 형태다 (§05 §12-1). 열 정의만
   다르므로 한 구현에 세 개의 설정을 넣는다.
   ───────────────────────────────────────────────────────────────────── */

interface ColumnSpec {
  key: string;
  label: string;
  required?: boolean;
  kind?: "text" | "number" | "email" | "tel";
  width?: number;
}

type MasterRow = { id: string; active: boolean; ref_count?: number };

/**
 * 열 정의가 문자열 키라서 셀 값은 런타임 조회다. 인덱스 시그니처를 타입에
 * 넣으면 Staff/Customer 같은 구체 인터페이스가 제약을 만족하지 못한다.
 */
const cellValue = (row: MasterRow, key: string): string => {
  const v = (row as unknown as Record<string, unknown>)[key];
  return v === null || v === undefined ? "" : String(v);
};

const SimpleSection = <T extends MasterRow>({
  title,
  resource,
  columns,
  canEdit,
  nav,
}: {
  title: string;
  resource: CrudResource<T>;
  columns: readonly ColumnSpec[];
  canEdit: boolean;
  nav: ScreenNav;
}) => {
  const [editing, setEditing] = useState<T | "new" | null>(null);
  const [removing, setRemoving] = useState<T | null>(null);
  const [busy, setBusy] = useState(false);

  const remove = async () => {
    if (!removing) return;
    setBusy(true);
    try {
      await resource.remove(removing.id);
      setRemoving(null);
      nav.toast("삭제했어요.");
    } catch (err) {
      setRemoving(null);
      // 409 = 참조 중. 건수를 다시 말하지 않는다 - 서버의 참조 집계와
      // 목록의 ref_count 는 세는 대상이 달라 숫자가 정당하게 다를 수 있다.
      const conflict = err instanceof Error && /referenced/i.test(err.message);
      nav.toast(
        conflict
          ? "이 항목을 참조하는 개발품이 있어요. 삭제 대신 비활성으로 전환해 주세요."
          : err instanceof Error
            ? err.message
            : "삭제하지 못했어요.",
        "error"
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card pad="sm">
      <div className="flex flex-wrap items-baseline justify-between gap-s3 px-s2 pb-s3">
        <h2 className="text-heading">
          {title}
          <span
            className="ml-s2 text-caption tabular-nums"
            data-num=""
            style={{ color: "var(--text-tertiary)" }}
          >
            {`${resource.data.length}건`}
          </span>
        </h2>
        {canEdit && (
          <Button
            variant="primary"
            size="sm"
            icon={<Icon name="plus" size={13} />}
            onClick={() => setEditing("new")}
          >
            {`${title} 추가`}
          </Button>
        )}
      </div>

      <DataBoundary
        loading={resource.loading}
        error={resource.error}
        onRetry={() => void resource.reload()}
        skeleton={<TableSkeleton rows={5} cols={columns.length + 3} />}
      >
        {resource.data.length === 0 ? (
          <EmptyState
            icon="db"
            title="등록된 항목이 없어요"
            description={`${title}를 등록하면 개발품 등록 화면의 선택 목록에 나타나요.`}
            action={
              canEdit ? (
                <Button variant="primary" size="md" onClick={() => setEditing("new")}>
                  {`${title} 추가`}
                </Button>
              ) : undefined
            }
          />
        ) : (
          <TableScroll>
            <Table label={title}>
              <THead>
                <tr>
                  {columns.map((c) => (
                    <Th key={c.key} width={c.width} num={c.kind === "number"}>
                      {c.label}
                    </Th>
                  ))}
                  <Th width={70}>활성</Th>
                  <Th width={70} num>
                    참조
                  </Th>
                  <Th width={140}>관리</Th>
                </tr>
              </THead>
              <tbody>
                {resource.data.map((row) => (
                  <Tr key={row.id}>
                    {columns.map((c) => (
                      <Td key={c.key} num={c.kind === "number"} strong={c.required}>
                        {cellValue(row, c.key) || EMPTY}
                      </Td>
                    ))}
                    <Td>
                      <Checkbox
                        checked={row.active}
                        disabled={!canEdit}
                        label={<span className="sr-only">활성</span>}
                        onChange={async (v) => {
                          try {
                            await resource.update(row.id, { active: v } as Partial<T>);
                          } catch (err) {
                            nav.toast(
                              err instanceof Error ? err.message : "저장하지 못했어요.",
                              "error"
                            );
                          }
                        }}
                      />
                    </Td>
                    <Td num>
                      <Num v={row.ref_count ?? 0} suffix="건" />
                    </Td>
                    <Td>
                      {canEdit && (
                        <span className="flex gap-s2">
                          <Button variant="secondary" size="sm" onClick={() => setEditing(row)}>
                            수정
                          </Button>
                          {/* 참조가 있으면 삭제를 아예 노출하지 않는다 (§04 §7). */}
                          {!row.ref_count && (
                            <Button variant="ghost" size="sm" onClick={() => setRemoving(row)}>
                              삭제하기
                            </Button>
                          )}
                        </span>
                      )}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        )}
      </DataBoundary>

      {editing && (
        <RowDialog
          title={editing === "new" ? `${title} 추가` : `${title} 수정`}
          columns={columns}
          row={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSave={async (values) => {
            try {
              if (editing === "new") {
                await resource.create({ ...values, active: true } as unknown as Omit<T, "id"> & {
                  id?: string;
                });
                nav.toast("등록했어요.");
              } else {
                await resource.update(editing.id, values as Partial<T>);
                nav.toast("저장했어요.");
              }
              setEditing(null);
            } catch (err) {
              nav.toast(err instanceof Error ? err.message : "저장하지 못했어요.", "error");
            }
          }}
        />
      )}

      <ConfirmDialog
        open={removing !== null}
        title="이 항목을 삭제할까요?"
        message="삭제하면 되돌릴 수 없어요. 개발품이 참조하고 있으면 삭제되지 않아요."
        confirmLabel="삭제하기"
        busy={busy}
        onCancel={() => setRemoving(null)}
        onConfirm={() => void remove()}
      />
    </Card>
  );
};

const RowDialog = ({
  title,
  columns,
  row,
  onClose,
  onSave,
}: {
  title: string;
  columns: readonly ColumnSpec[];
  row: MasterRow | null;
  onClose: () => void;
  onSave: (values: Record<string, string | number>) => Promise<void>;
}) => {
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(columns.map((c) => [c.key, row ? cellValue(row, c.key) : ""]))
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const e: Record<string, string> = {};
    for (const c of columns) {
      if (c.required && !values[c.key]?.trim()) e[c.key] = "필수 항목을 입력해 주세요.";
    }
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    setBusy(true);
    const out: Record<string, string | number> = {};
    for (const c of columns) {
      out[c.key] =
        c.kind === "number"
          ? values[c.key] === ""
            ? 0
            : Number.parseInt(values[c.key], 10)
          : values[c.key].trim();
    }
    await onSave(out);
    setBusy(false);
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={title}
      width={520}
      footer={
        <>
          <Button variant="secondary" size="md" onClick={onClose} disabled={busy}>
            취소
          </Button>
          <Button variant="primary" size="md" onClick={submit} loading={busy}>
            저장
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-s4 sm:grid-cols-2">
        {columns.map((c) => (
          <Field key={c.key} label={c.label} required={c.required} error={errors[c.key]}>
            {(p) => (
              <TextInput
                {...p}
                type={c.kind === "number" ? "number" : c.kind === "email" ? "email" : "text"}
                align={c.kind === "number" ? "right" : "left"}
                value={values[c.key] ?? ""}
                onChange={(v) => setValues((prev) => ({ ...prev, [c.key]: v }))}
              />
            )}
          </Field>
        ))}
      </div>
    </Dialog>
  );
};

/* ── 리드타임 템플릿 (§16) ──────────────────────────────────────── */

const LeadTimeSection = ({ canEdit, nav }: { canEdit: boolean; nav: ScreenNav }) => {
  const [offsets, setOffsets] = useState<Record<string, string>>({});
  const [seed, setSeed] = useState(0);
  const res = useLeadTimeTemplates();

  const tpl = res.data.find((t) => t.isDefault) ?? res.data[0];
  if (tpl && seed !== res.data.length + tpl.offsets.length) {
    setSeed(res.data.length + tpl.offsets.length);
    setOffsets(Object.fromEntries(tpl.offsets.map((o) => [o.milestoneKey, String(o.offsetDays)])));
  }

  return (
    <Card pad="sm">
      <div className="px-s2 pb-s3">
        <h2 className="text-heading">표준 리드타임</h2>
        <p className="mt-s1 text-caption" style={{ color: "var(--text-tertiary)" }}>
          SOP 로부터 며칠 전인지(D-N)를 정합니다. 결과가 토·일이나 공휴일이면 직전 영업일로
          앞당겨집니다.
        </p>
      </div>
      <DataBoundary
        loading={res.loading}
        error={res.error}
        onRetry={() => void res.reload()}
        skeleton={<TableSkeleton rows={8} cols={3} />}
      >
        {!tpl ? (
          <EmptyState icon="clock" title="등록된 템플릿이 없어요" />
        ) : (
          <>
            <TableScroll>
              <Table label="표준 리드타임">
                <THead>
                  <tr>
                    <Th width={180}>마일스톤</Th>
                    <Th width={140}>라벨</Th>
                    <Th width={140} num>
                      D-N (일)
                    </Th>
                  </tr>
                </THead>
                <tbody>
                  {tpl.offsets.map((o) => (
                    <Tr key={o.milestoneKey}>
                      <Td strong>
                        {MILESTONE_DEFS.find((m) => m.key === o.milestoneKey)?.label ??
                          o.milestoneKey}
                      </Td>
                      <Td>{o.label || EMPTY}</Td>
                      <Td num>
                        {o.milestoneKey === "sop" ? (
                          <span className="tabular-nums" data-num="">
                            0
                          </span>
                        ) : (
                          <TextInput
                            type="number"
                            align="right"
                            disabled={!canEdit}
                            ariaLabel={`${o.label || o.milestoneKey} D-N (일)`}
                            value={offsets[o.milestoneKey] ?? ""}
                            onChange={(v) =>
                              setOffsets((prev) => ({ ...prev, [o.milestoneKey]: v }))
                            }
                          />
                        )}
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableScroll>
            {canEdit && (
              <div className="flex justify-end px-s2 pt-s3">
                <Button
                  variant="primary"
                  size="md"
                  onClick={async () => {
                    try {
                      await res.update(tpl.id, {
                        offsets: tpl.offsets.map((o) => ({
                          ...o,
                          offsetDays:
                            o.milestoneKey === "sop"
                              ? 0
                              : Math.max(
                                  0,
                                  Number.parseInt(offsets[o.milestoneKey] ?? "0", 10) || 0
                                ),
                        })),
                      });
                      nav.toast("저장했어요.");
                    } catch (err) {
                      nav.toast(err instanceof Error ? err.message : "저장하지 못했어요.", "error");
                    }
                  }}
                >
                  저장
                </Button>
              </div>
            )}
          </>
        )}
      </DataBoundary>
    </Card>
  );
};

/* ── 마일스톤 라벨 (9건 고정) ───────────────────────────────────── */

const LabelSection = ({ canEdit, nav }: { canEdit: boolean; nav: ScreenNav }) => {
  const res = useMilestoneLabels();
  const [draft, setDraft] = useState<MilestoneLabel[]>([]);
  const [busy, setBusy] = useState(false);
  const [seed, setSeed] = useState(0);
  if (res.data.length > 0 && seed !== res.data.length) {
    setSeed(res.data.length);
    setDraft(res.data.map((l) => ({ ...l })));
  }

  return (
    <Card>
      <h2 className="text-heading">마일스톤 라벨</h2>
      <p className="mt-s1 text-caption" style={{ color: "var(--text-tertiary)" }}>
        9단계는 추가·삭제할 수 없습니다. 라벨만 바꿀 수 있고, 변경하면 기존 개발품에도 반영됩니다.
      </p>
      <DataBoundary loading={res.loading} error={res.error} onRetry={() => void res.reload()}>
        <div className="mt-s4 grid grid-cols-1 gap-s4 sm:grid-cols-3">
          {draft.map((l, i) => (
            <Field key={l.key} label={`${l.seq + 1}. ${l.key}`}>
              {(p) => (
                <TextInput
                  {...p}
                  value={l.label}
                  disabled={!canEdit}
                  maxLength={40}
                  onChange={(v) =>
                    setDraft((prev) => prev.map((x, xi) => (xi === i ? { ...x, label: v } : x)))
                  }
                />
              )}
            </Field>
          ))}
        </div>
        {canEdit && draft.length === 9 && (
          <div className="mt-s4 flex justify-end">
            <Button
              variant="primary"
              size="md"
              loading={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await putMilestoneLabels(draft);
                  await res.reload();
                  nav.toast("저장했어요.");
                } catch (err) {
                  nav.toast(err instanceof Error ? err.message : "저장하지 못했어요.", "error");
                } finally {
                  setBusy(false);
                }
              }}
            >
              저장
            </Button>
          </div>
        )}
      </DataBoundary>
    </Card>
  );
};

/* ── 공휴일 (§16 영업일 보정) ───────────────────────────────────── */

const HolidaySection = ({ canEdit, nav }: { canEdit: boolean; nav: ScreenNav }) => {
  const [year, setYear] = useState("");
  const res = useHolidays(year ? Number.parseInt(year, 10) : undefined);
  const [date, setDate] = useState("");
  const [name, setName] = useState("");
  const thisYear = new Date().getFullYear();

  return (
    <Card pad="sm">
      <div className="flex flex-wrap items-end justify-between gap-s3 px-s2 pb-s3">
        <div>
          <h2 className="text-heading">공휴일</h2>
          <p className="mt-s1 text-caption" style={{ color: "var(--text-tertiary)" }}>
            비어 있어도 동작합니다. 그 경우 주말만 영업일 보정에 반영돼요.
          </p>
        </div>
        <span className="w-[9rem]">
          <Select
            value={year}
            onChange={setYear}
            options={[thisYear - 1, thisYear, thisYear + 1, thisYear + 2].map((y) => ({
              value: String(y),
              label: `${y}년`,
            }))}
            ariaLabel="연도로 조회"
            placeholder="전체 연도"
          />
        </span>
      </div>

      {canEdit && (
        <div className="flex flex-wrap items-end gap-s3 px-s2 pb-s4">
          <span className="w-[11rem]">
            <Field label="날짜" required>
              {(p) => <DateField {...p} value={date} onChange={setDate} />}
            </Field>
          </span>
          <span className="w-[12rem]">
            <Field label="명칭">
              {(p) => <TextInput {...p} value={name} onChange={setName} maxLength={30} />}
            </Field>
          </span>
          <Button
            variant="primary"
            size="md"
            disabled={!date}
            onClick={async () => {
              try {
                await res.add(date, name.trim());
                setDate("");
                setName("");
                nav.toast("등록했어요.");
              } catch (err) {
                nav.toast(err instanceof Error ? err.message : "저장하지 못했어요.", "error");
              }
            }}
          >
            추가
          </Button>
        </div>
      )}

      <DataBoundary
        loading={res.loading}
        error={res.error}
        onRetry={() => void res.reload()}
        skeleton={<TableSkeleton rows={4} cols={3} />}
      >
        {res.data.length === 0 ? (
          <p className="px-s2 pb-s2 text-label" style={{ color: "var(--text-tertiary)" }}>
            등록된 공휴일이 없어요. 주말만 영업일 보정에 반영됩니다.
          </p>
        ) : (
          <TableScroll>
            <Table label="공휴일">
              <THead>
                <tr>
                  <Th width={140} num>
                    날짜
                  </Th>
                  <Th>명칭</Th>
                  <Th width={100}>관리</Th>
                </tr>
              </THead>
              <tbody>
                {res.data.map((h) => (
                  <Tr key={h.id}>
                    <Td num>{h.date}</Td>
                    <Td>{h.name || EMPTY}</Td>
                    <Td>
                      {canEdit && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={async () => {
                            try {
                              await res.remove(h.id);
                              nav.toast("삭제했어요.");
                            } catch (err) {
                              nav.toast(
                                err instanceof Error ? err.message : "삭제하지 못했어요.",
                                "error"
                              );
                            }
                          }}
                        >
                          삭제하기
                        </Button>
                      )}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        )}
      </DataBoundary>
    </Card>
  );
};

/* ── 판정 설정 (§10 임계값 + 진행률 가중치) ─────────────────────── */

const JudgeSection = ({ canEdit, nav }: { canEdit: boolean; nav: ScreenNav }) => {
  const res = useSettings();
  const [draft, setDraft] = useState<CompanySettings | null>(null);
  const [busy, setBusy] = useState(false);
  const [seed, setSeed] = useState("");
  if (res.data && seed !== res.data.timezone + res.data.imminentDays) {
    setSeed(res.data.timezone + res.data.imminentDays);
    setDraft({ ...res.data, progressWeights: { ...res.data.progressWeights } });
  }

  const sum = draft
    ? MILESTONE_DEFS.reduce((acc, m) => acc + (draft.progressWeights[m.key] ?? 0), 0)
    : 0;
  const weightsOk = sum === 100;

  return (
    <Card>
      <h2 className="text-heading">판정 설정</h2>
      <p className="mt-s1 text-caption" style={{ color: "var(--text-tertiary)" }}>
        여기서 바꾼 기준이 모든 화면의 상태·D-Day·알림 판정에 적용됩니다.
      </p>
      <DataBoundary loading={res.loading} error={res.error} onRetry={() => void res.reload()}>
        {draft && (
          <>
            <div className="mt-s4 grid grid-cols-1 gap-s4 sm:grid-cols-2">
              <Field
                label="임박 임계값 (일)"
                hint="기본 3. 마일스톤·준비항목이 주의로 바뀌는 시점."
              >
                {(p) => (
                  <TextInput
                    {...p}
                    type="number"
                    align="right"
                    disabled={!canEdit}
                    value={String(draft.imminentDays)}
                    onChange={(v) =>
                      setDraft({ ...draft, imminentDays: Number.parseInt(v, 10) || 0 })
                    }
                  />
                )}
              </Field>
              <Field
                label="SOP 임박 임계값 (일)"
                hint="기본 30. 알림 카드 라벨의 D-N 도 이 값을 씁니다."
              >
                {(p) => (
                  <TextInput
                    {...p}
                    type="number"
                    align="right"
                    disabled={!canEdit}
                    value={String(draft.sopImminentDays)}
                    onChange={(v) =>
                      setDraft({ ...draft, sopImminentDays: Number.parseInt(v, 10) || 0 })
                    }
                  />
                )}
              </Field>
              <Field
                label="생산 준비 조회 기간 (일)"
                hint="기본 7. 오늘부터 며칠 뒤 생산까지 미입고를 경고할지."
              >
                {(p) => (
                  <TextInput
                    {...p}
                    type="number"
                    align="right"
                    disabled={!canEdit}
                    value={String(draft.prodPrepLookaheadDays)}
                    onChange={(v) =>
                      setDraft({ ...draft, prodPrepLookaheadDays: Number.parseInt(v, 10) || 0 })
                    }
                  />
                )}
              </Field>
              <Field
                label="영업일 보정 방향"
                hint="기본 backward. 자동생성 결과가 비영업일일 때 앞당길지 미룰지."
              >
                {(p) => (
                  <Select
                    {...p}
                    value={draft.businessDayAdjust}
                    disabled={!canEdit}
                    onChange={(v) =>
                      setDraft({ ...draft, businessDayAdjust: v as "backward" | "forward" })
                    }
                    options={[
                      { value: "backward", label: "직전 영업일로 앞당김" },
                      { value: "forward", label: "다음 영업일로 미룸" },
                    ]}
                  />
                )}
              </Field>
              <Field
                label="표시 시간대"
                hint="읽기 전용입니다. 모든 날짜 판정은 Asia/Seoul 기준입니다."
              >
                {(p) => <TextInput {...p} value={draft.timezone} disabled />}
              </Field>
            </div>

            <div className="mt-s6">
              <div className="flex flex-wrap items-baseline gap-s3">
                <h3 className="text-heading">진행률 가중치</h3>
                <span
                  className="text-label font-semibold tabular-nums"
                  data-num=""
                  style={{ color: weightsOk ? "var(--text-secondary)" : "var(--state-late)" }}
                >
                  {`합계 ${sum}`}
                </span>
                {!weightsOk && (
                  <span className="text-micro" style={{ color: "var(--state-late)" }}>
                    합계가 100이어야 합니다.
                  </span>
                )}
              </div>
              <div className="mt-s3 grid grid-cols-1 gap-s4 sm:grid-cols-3">
                {MILESTONE_DEFS.map((m) => (
                  <Field key={m.key} label={m.label}>
                    {(p) => (
                      <TextInput
                        {...p}
                        type="number"
                        align="right"
                        disabled={!canEdit}
                        value={String(draft.progressWeights[m.key] ?? 0)}
                        onChange={(v) =>
                          setDraft({
                            ...draft,
                            progressWeights: {
                              ...draft.progressWeights,
                              [m.key]: Number.parseInt(v, 10) || 0,
                            },
                          })
                        }
                      />
                    )}
                  </Field>
                ))}
              </div>
            </div>

            {canEdit && (
              <div className="mt-s5 flex items-center justify-end gap-s3">
                <Button
                  variant="ghost"
                  size="md"
                  onClick={() => setDraft({ ...draft, progressWeights: { ...DEFAULT_WEIGHTS } })}
                >
                  기본 가중치로
                </Button>
                <Button
                  variant="primary"
                  size="md"
                  disabled={!weightsOk}
                  loading={busy}
                  onClick={async () => {
                    setBusy(true);
                    try {
                      await updateSettings({
                        imminentDays: draft.imminentDays,
                        sopImminentDays: draft.sopImminentDays,
                        prodPrepLookaheadDays: draft.prodPrepLookaheadDays,
                        businessDayAdjust: draft.businessDayAdjust,
                        progressWeights: draft.progressWeights as Record<MilestoneKey, number>,
                      });
                      await res.reload();
                      nav.toast("저장했어요. 변경된 기준이 모든 화면에 적용돼요.");
                    } catch (err) {
                      nav.toast(err instanceof Error ? err.message : "저장하지 못했어요.", "error");
                    } finally {
                      setBusy(false);
                    }
                  }}
                >
                  저장
                </Button>
              </div>
            )}
          </>
        )}
      </DataBoundary>
    </Card>
  );
};

/* ── 사용자 (역할 · 담당자 연결) ────────────────────────────────── */

const UserSection = ({
  canEdit,
  nav,
  staff,
}: {
  canEdit: boolean;
  nav: ScreenNav;
  staff: readonly Staff[];
}) => {
  const res = useUsers();
  return (
    <Card pad="sm">
      <div className="px-s2 pb-s3">
        <h2 className="text-heading">사용자</h2>
        <p className="mt-s1 text-caption" style={{ color: "var(--text-tertiary)" }}>
          담당자를 연결하면 그 사용자의 내 업무 화면이 채워집니다. 마지막 관리자의 권한은 내릴 수
          없어요.
        </p>
      </div>
      <DataBoundary
        loading={res.loading}
        error={res.error}
        onRetry={() => void res.reload()}
        skeleton={<TableSkeleton rows={4} cols={4} />}
      >
        <TableScroll>
          <Table label="사용자">
            <THead>
              <tr>
                <Th width={220}>이메일</Th>
                <Th width={120}>이름</Th>
                <Th width={130}>역할</Th>
                <Th width={160}>담당자 연결</Th>
              </tr>
            </THead>
            <tbody>
              {res.data.map((u) => (
                <Tr key={u.uid}>
                  <Td>{u.email || EMPTY}</Td>
                  <Td strong>{u.name || EMPTY}</Td>
                  <Td>
                    <Select
                      value={u.role}
                      disabled={!canEdit}
                      ariaLabel={`${u.email} 역할`}
                      options={[
                        { value: "admin", label: "관리자" },
                        { value: "member", label: "일반" },
                      ]}
                      onChange={async (v) => {
                        try {
                          await updateUser(u.uid, { role: v as "admin" | "member" });
                          await res.reload();
                          nav.toast("저장했어요.");
                        } catch (err) {
                          nav.toast(
                            err instanceof Error ? err.message : "저장하지 못했어요.",
                            "error"
                          );
                        }
                      }}
                    />
                  </Td>
                  <Td>
                    <Select
                      value={u.staffId ?? ""}
                      disabled={!canEdit}
                      ariaLabel={`${u.email} 담당자 연결`}
                      placeholder="연결 없음"
                      options={staff.map((s) => ({ value: s.id, label: s.name }))}
                      onChange={async (v) => {
                        try {
                          await updateUser(u.uid, { staffId: v });
                          await res.reload();
                          nav.toast("저장했어요.");
                        } catch (err) {
                          nav.toast(
                            err instanceof Error ? err.message : "저장하지 못했어요.",
                            "error"
                          );
                        }
                      }}
                    />
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </TableScroll>
      </DataBoundary>
    </Card>
  );
};
